// ============================================================
// StepParents Component
// ============================================================

import { TextField, MenuItem, Grid, Typography, Divider, FormControlLabel, Checkbox, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { 
  nameRegex, 
  digitsOnlyRegex, 
  decimalRegex, 
  createRestrictedHandler 
} from '@/utils/inputRestrictions';

export function StepParents({ formik }) {
  const { values, errors, touched, handleChange, handleBlur } = formik;

  const [sameAsFather, setSameAsFather] = useState(
    Boolean(
      values.guardian_name &&
      values.father_name &&
      values.guardian_name === values.father_name &&
      values.guardian_mobile === values.father_mobile
    )
  );

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setSameAsFather(isChecked);
    if (isChecked) {
      formik.setFieldValue('guardian_name', values.father_name || '');
      formik.setFieldValue('guardian_relation', 'Father');
      formik.setFieldValue('guardian_mobile', values.father_mobile || '');
    }
  };

  useEffect(() => {
    if (sameAsFather) {
      formik.setFieldValue('guardian_name', values.father_name || '');
      formik.setFieldValue('guardian_relation', 'Father');
      formik.setFieldValue('guardian_mobile', values.father_mobile || '');
    }
  }, [values.father_name, values.father_mobile, sameAsFather]);

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle1" color="primary">Father's Details</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Father's Name *"
          name="father_name"
          value={values.father_name}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          error={touched.father_name && Boolean(errors.father_name)}
          helperText={touched.father_name && errors.father_name}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Father's Mobile *"
          name="father_mobile"
          value={values.father_mobile}
          onChange={createRestrictedHandler(handleChange, digitsOnlyRegex, 10)}
          onBlur={handleBlur}
          error={touched.father_mobile && Boolean(errors.father_mobile)}
          helperText={touched.father_mobile && errors.father_mobile}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Father's Email"
          name="father_email"
          type="email"
          value={values.father_email}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Father's Occupation"
          name="father_occupation"
          value={values.father_occupation}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          error={touched.father_occupation && Boolean(errors.father_occupation)}
          helperText={touched.father_occupation && errors.father_occupation}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1" color="primary">Mother's Details</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Mother's Name"
          name="mother_name"
          value={values.mother_name}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Mother's Mobile"
          name="mother_mobile"
          value={values.mother_mobile}
          onChange={createRestrictedHandler(handleChange, digitsOnlyRegex, 10)}
          onBlur={handleBlur}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Mother's Email"
          name="mother_email"
          type="email"
          value={values.mother_email}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Mother's Occupation"
          name="mother_occupation"
          value={values.mother_occupation}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" color="primary">Guardian & Other Details</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsFather}
                onChange={handleCheckboxChange}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2" fontWeight={500}>Set Guardian details same as Father</Typography>}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Guardian's Name *"
          name="guardian_name"
          value={values.guardian_name}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          error={touched.guardian_name && Boolean(errors.guardian_name)}
          helperText={touched.guardian_name && errors.guardian_name}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Guardian's Relation *"
          name="guardian_relation"
          value={values.guardian_relation}
          onChange={createRestrictedHandler(handleChange, nameRegex)}
          onBlur={handleBlur}
          error={touched.guardian_relation && Boolean(errors.guardian_relation)}
          helperText={touched.guardian_relation && errors.guardian_relation}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Guardian's Mobile *"
          name="guardian_mobile"
          value={values.guardian_mobile}
          onChange={createRestrictedHandler(handleChange, digitsOnlyRegex, 10)}
          onBlur={handleBlur}
          error={touched.guardian_mobile && Boolean(errors.guardian_mobile)}
          helperText={touched.guardian_mobile && errors.guardian_mobile}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Annual Family Income"
          name="annual_income"
          value={values.annual_income}
          onChange={createRestrictedHandler(handleChange, decimalRegex)}
          onBlur={handleBlur}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          select
          fullWidth
          label="Primary Login *"
          name="primary_login"
          value={values.primary_login || 'FATHER'}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.primary_login && Boolean(errors.primary_login)}
          helperText={touched.primary_login && errors.primary_login}
        >
          <MenuItem value="FATHER">Father</MenuItem>
          <MenuItem value="MOTHER">Mother</MenuItem>
          <MenuItem value="GUARDIAN">Guardian</MenuItem>
        </TextField>
      </Grid>
    </Grid>
  );
}
